import * as React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { useAuth } from "@/auth/useAuth";
import { Button, FormBox } from "@/components/ui-kit";
import { authApi } from "@/lib/auth-api";
import { getApiErrorMessage } from "@/lib/error-codes";
import { ArrowRightIcon } from "@/components/icons";

const restorePasswordSchema = z
  .object({
    email: z.email("Введите корректный email"),
    password: z.string().min(6, "Пароль должен быть не короче 6 символов"),
    confirmPassword: z.string().min(1, "Подтвердите пароль"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Пароли не совпадают.",
    path: ["confirmPassword"],
  });

export function RestorePasswordPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated } = useAuth();

  const [form, setForm] = React.useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    const parsed = restorePasswordSchema.safeParse(form);
    if (!parsed.success) {
      const nextErrors: {
        email?: string;
        password?: string;
        confirmPassword?: string;
      } = {};

      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "email" || key === "password" || key === "confirmPassword") {
          nextErrors[key] = issue.message;
        }
      }

      setFieldErrors(nextErrors);
      return;
    }

    setIsLoading(true);
    try {
      await authApi.restorePassword({
        email: parsed.data.email,
        token,
        password: parsed.data.password,
      });
      setSuccess("Пароль обновлен. Теперь можно войти.");
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className="mb-8 text-[48px] leading-[52px] font-bold text-primary">
        Придумайте пароль
      </h1>

      <form onSubmit={onSubmit} noValidate className="w-full max-w-[541px] flex flex-col gap-6">
        <FormBox
          id="restore-email"
          label="Почта"
          type="email"
          defaultValue={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          autoComplete="email"
        />
        {fieldErrors.email ? <p className="text-sm text-destructive">{fieldErrors.email}</p> : null}
        <FormBox
          id="restore-password"
          label="Пароль"
          type="password"
          defaultValue={form.password}
          onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
          autoComplete="new-password"
        />
        {fieldErrors.password ? <p className="text-sm text-destructive">{fieldErrors.password}</p> : null}
        <FormBox
          id="restore-confirm-password"
          label="Пароль еще раз"
          type="password"
          defaultValue={form.confirmPassword}
          onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
          autoComplete="new-password"
        />
        {fieldErrors.confirmPassword ? <p className="text-sm text-destructive">{fieldErrors.confirmPassword}</p> : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {success ? <p className="text-sm text-primary">{success}</p> : null}

        <Button type="submit" disabled={isLoading || !form.email || !form.password || !form.confirmPassword} endIcon={<ArrowRightIcon size={24} />}>
          {isLoading ? "Сохраняем..." : "Изменить пароль"}
        </Button>
      </form>
    </>
  );
}
